import { http, HttpResponse } from 'msw';
import type { Hypotheses, PlanBootstrap, PlanGeoData, Scenario, ScenarioResults } from './types';

// ─── Demo geography: real UrbanMap export (Gentilly + Le Kremlin-Bicêtre) ───
// `data/gentilly.json` is generated by `data/build_data.py` from the raw UM GeoJSON exports
// (buildings BDNB, heat networks, SNCU delivery points). It is lazy-loaded so the main bundle stays light.

type Row = (string | number | null)[];
type Line = number[];

/** Shape of `data/gentilly.json` — see the "Compact encoding" section of `build_data.py`. */
interface CompactGeo {
  origin: [number, number];
  scale: number;
  addressSuffix: string;
  buildings: { fields: string[]; rows: Row[]; geom: Line[][] };
  dict: Record<string, string[]>;
  networks: (Record<string, string | number> & { lines: Line[] })[];
  delivery: { fields: string[]; rows: Row[]; geom: Line[] };
  perimeter: Line;
  bounds: [[number, number], [number, number]];
}

interface RawGeo {
  buildings: GeoJSON.FeatureCollection;
  networks: GeoJSON.FeatureCollection;
  deliveryPoints: GeoJSON.FeatureCollection;
  perimeter: [number, number][];
  bounds: [[number, number], [number, number]];
}

function decodeGeo(c: CompactGeo): RawGeo {
  const line = (d: Line): [number, number][] => {
    const out: [number, number][] = [];
    let x = 0, y = 0;
    for (let i = 0; i < d.length; i += 2) {
      x += d[i]; y += d[i + 1];
      out.push([+(c.origin[0] + x / c.scale).toFixed(5), +(c.origin[1] + y / c.scale).toFixed(5)]);
    }
    return out;
  };
  const props = (fields: string[], row: Row, id: number) => {
    const p: Record<string, unknown> = { id };
    fields.forEach((k, i) => {
      const v = row[i];
      if (v === null || v === undefined) return;
      p[k] = c.dict[k] && typeof v === 'number' ? c.dict[k][v] : k === 'address' && !/\d{5}/.test(String(v)) ? `${v}${c.addressSuffix}` : v;
    });
    return p;
  };
  const fc = (features: GeoJSON.Feature[]): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features });
  return {
    buildings: fc(c.buildings.rows.map((row, i) => {
      const polys = c.buildings.geom[i].map((r) => [line(r)]);
      const p = props(c.buildings.fields, row, i + 1);
      p.inside = 1;
      p.usageLabel ??= p.usage;
      return {
        type: 'Feature', id: i + 1, properties: p,
        geometry: polys.length > 1 ? { type: 'MultiPolygon', coordinates: polys } : { type: 'Polygon', coordinates: polys[0] },
      };
    })),
    networks: fc(c.networks.map(({ lines, ...p }) => ({
      type: 'Feature', id: p.id as number, properties: p, geometry: { type: 'MultiLineString', coordinates: lines.map(line) },
    }))),
    deliveryPoints: fc(c.delivery.rows.map((row, i) => {
      const p = props(c.delivery.fields, row, i + 1);
      return { type: 'Feature', id: i + 1, properties: p, geometry: { type: 'Point', coordinates: line(c.delivery.geom[i])[0] } };
    })),
    perimeter: line(c.perimeter),
    bounds: c.bounds,
  };
}

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

function polygon(ring: [number, number][], properties: Record<string, unknown>): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [{ type: 'Feature', id: 1, properties: { id: 1, ...properties }, geometry: { type: 'Polygon', coordinates: [ring] } }] };
}

function toGeo(raw: RawGeo): PlanGeoData {
  const [[x0, y0], [x1, y1]] = raw.bounds;
  const pad = 0.004;
  // Third-party UM layer, deliberately larger than (and not clipped to) the project perimeter.
  const zone: [number, number][] = [
    [x0 - pad, y0 + 0.004], [x0 + 0.008, y0 - pad], [x1 + pad, y0 - 0.001], [x1 + pad, y1 - 0.002], [x1 - 0.01, y1 + pad], [x0 - pad, y1 + 0.001], [x0 - pad, y0 + 0.004],
  ];
  return {
    buildings: raw.buildings,
    roads: EMPTY,
    pipes: raw.networks,
    deliveryPoints: raw.deliveryPoints,
    perimeter: polygon(raw.perimeter, {}),
    classificationZone: polygon(zone, { name: 'Zone de développement prioritaire — réseau Arcueil-Gentilly' }),
    center: [(x0 + x1) / 2, (y0 + y1) / 2],
    bounds: raw.bounds,
  };
}

let GEO: PlanGeoData | null = null;
export let PERIMETER_BUILDINGS: GeoJSON.Feature[] = [];

async function loadGeo(): Promise<PlanGeoData> {
  if (!GEO) {
    const raw = decodeGeo((await import('./data/gentilly.json')).default as unknown as CompactGeo);
    GEO = toGeo(raw);
    PERIMETER_BUILDINGS = GEO.buildings.features.filter((f) => f.properties?.inside === 1);
  }
  return GEO;
}

// ─── Simulation (deterministic, offline) ────────────────────────────────────

export function computeResults(h: Hypotheses): ScenarioResults {
  const eligible = PERIMETER_BUILDINGS
    .filter((f) => (f.properties?.heat as number) >= h.minDemand && h.usages.includes(f.properties?.usage as string))
    .sort((a, b) => (b.properties?.heat as number) - (a.properties?.heat as number));
  const count = Math.round((eligible.length * h.connectionRate) / 100);
  const connected = eligible.slice(0, count);
  const mwh = connected.reduce((s, f) => s + (f.properties?.heat as number), 0);
  const tempFactor = h.temperature === 'Basse' ? 0.92 : h.temperature === 'Haute' ? 1.1 : 1;
  const networkKm = Math.round(count * 0.085 * tempFactor * 10) / 10;
  const density = networkKm > 0 ? Math.round((mwh / (networkKm * 1000)) * 100) / 100 : 0;
  const capexMEur = Math.round(((networkKm * 1000 * h.capexPerMeter) / 1e6 + count * 0.045) * (1 - h.subsidyRate / 100) * 10) / 10;
  const renewableRate = Math.min(100, h.biomass + h.geothermal + h.recovery);
  const revenue = (mwh * h.heatPrice) / 1e6;
  // Simplified mock: margin ~12 % of revenue over capex, bounded to a plausible 2–14 % range.
  const irr = capexMEur > 0 ? Math.round(Math.min(14, Math.max(2, ((revenue * 0.12) / capexMEur) * 100 - h.discountRate / 2)) * 10) / 10 : 0;
  const years = h.horizon === '2030' ? ['2027', '2028', '2030'] : h.horizon === '2035' ? ['2027', '2030', '2035'] : ['2027', '2032', '2040'];
  const split = [0.45, 0.35, 0.2];
  return {
    connectedBuildings: connected.map((f) => f.properties?.functionalId as string),
    networkKm,
    deliveredGWh: Math.round(mwh / 100) / 10,
    renewableRate,
    capexMEur,
    irr,
    density,
    phases: years.map((year, i) => ({
      year,
      buildings: Math.round(count * split[i]),
      km: Math.round(networkKm * split[i] * 10) / 10,
      capexMEur: Math.round(capexMEur * split[i] * 10) / 10,
    })),
  };
}

export const DEFAULT_HYPOTHESES: Hypotheses = {
  minDemand: 400,
  connectionRate: 60,
  usages: ['Habitat collectif', 'Tertiaire', 'Enseignement', 'Santé', 'Commerce'],
  temperature: 'Moyenne',
  minDensity: 1.5,
  biomass: 45,
  geothermal: 15,
  recovery: 10,
  heatPrice: 92,
  capexPerMeter: 2400,
  subsidyRate: 30,
  discountRate: 5,
  horizon: '2035',
};

function scenario(id: string, name: string, h: Hypotheses, status: Scenario['status'], updatedAt: string): Scenario {
  return { id, name, status, progress: status === 'done' ? 100 : 0, hypotheses: h, results: status === 'done' ? computeResults(h) : undefined, updatedAt };
}

const buildScenarios = (): Scenario[] => [
  scenario('sc-1', 'Référence — densification', DEFAULT_HYPOTHESES, 'done', '14/10/2026 10:12'),
  scenario('sc-2', 'Ambitieux — 80 % EnR&R', {
    ...DEFAULT_HYPOTHESES, minDemand: 250, connectionRate: 80, temperature: 'Basse', biomass: 50, geothermal: 25, recovery: 10, subsidyRate: 40, horizon: '2040',
  }, 'done', '14/10/2026 11:40'),
  scenario('sc-3', 'Sobre — gros consommateurs', {
    ...DEFAULT_HYPOTHESES, minDemand: 900, connectionRate: 90, usages: ['Tertiaire', 'Santé', 'Enseignement'], horizon: '2030',
  }, 'draft', '14/10/2026 12:02'),
];

const DRIFT_CHANGES = [
  'Consommation mise à jour (BDNB 2026)',
  'Usage modifié : Tertiaire → Santé',
  'Surface corrigée (+320 m²)',
  'Mode de chauffage : Individuel → Collectif',
];

/** 23 buildings whose UrbanMap record changed after the snapshot (information only, no sync). */
const buildDrift = () => PERIMETER_BUILDINGS.filter((f) => (f.properties?.heat as number) > 0).filter((_, i) => i % 7 === 3).slice(0, 23).map((f, i) => ({
  functionalId: f.properties?.functionalId as string,
  change: DRIFT_CHANGES[i % DRIFT_CHANGES.length],
}));

const buildBootstrap = (geo: PlanGeoData): PlanBootstrap => ({
  geo,
  project: {
    id: 'pl-gentilly',
    name: 'Gentilly — extension réseau Arcueil-Gentilly',
    perimeterLabel: 'Gentilly (94250)',
    networkKind: 'existing',
    snapshotDate: '14/10/2026',
    owner: 'Camille Martin',
    sharedWith: ['Julien Bernard', 'Équipe Études Val-de-Marne'],
    umDrift: buildDrift(),
    modifiedBuildings: [],
  },
  otherProjects: [
    { id: 'pl-gentilly-v2', name: 'Gentilly — variante Gentilly + Kremlin-Bicêtre', snapshotDate: '02/09/2026', owner: 'Camille Martin' },
    { id: 'pl-villejuif', name: 'Villejuif — interconnexion SEMHACH', snapshotDate: '18/06/2026', owner: 'Julien Bernard' },
  ],
  scenarios: buildScenarios(),
  umSelections: ['Gentilly (94250)', 'Kremlin-Bicêtre — prospects 2026', 'Hôpitaux Val-de-Marne'],
});

export const handlers = [
  http.get('/api/plan/projects/:id', async () => HttpResponse.json(buildBootstrap(await loadGeo()))),
];
